import React from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { Square, Warning } from '@phosphor-icons/react';
import { Loader } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import Timer from './Timer';
import type { RecorderState } from '../../store';
import AudioWaveform from './AudioWaveform';
import ControlButtons from './ControlButtons';
import CameraPreview from '../MediaPreview/Camera';
import Display from '../MediaPreview/Display';
import { streams } from '../utils/streamUtils';
import { stopRecordingThunk } from '../../thunk';

interface RecorderControlsProps {
  onToggleNotes: () => void;
  showNotes: boolean;
  isPopupMode?: boolean;
}

const RecorderControls: React.FC<RecorderControlsProps> = ({
  onToggleNotes,
  showNotes,
  isPopupMode = false,
}) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { status, selectedDevices, settings } = useSelector(
    (state: RecorderState) => state.recorder,
  );
  const isRecording = status === 'recording';
  const isPaused = status === 'paused';
  const isStopped = status === 'stopped';
  const isSaving = status === 'saving';
  const showTimer = isRecording || isPaused;
  const showControls = isRecording || isPaused;
  const showLoader = isSaving;
  const isControlDisabled = !['recording', 'paused'].includes(status);

  const stopRecordingHandler = () => {
    dispatch(stopRecordingThunk());
  };

  // Check if microphone is muted or no audio
  const isMicrophoneMuted =
    selectedDevices.audioinput.deviceId === 'none' ||
    !selectedDevices.audioinput.deviceId ||
    !settings?.audioSettings?.microphoneAudio;

  const isSmall = typeof window !== 'undefined' && window.innerWidth < 640;

  // In popup mode, we don't show the camera preview
  const showCameraPreview =
    (status === 'recording' || status === 'paused') && !isSmall && !isPopupMode;

  // In popup mode, we show a smaller display preview
  const showDisplayPreview =
    (status === 'recording' || status === 'paused') && !isPopupMode;

  // Check if display media stream has video tracks
  const hasDisplayVideoTracks =
    streams.displayMediaStream?.getVideoTracks().length > 0 &&
    !settings.audioSettings.isTabAudioOnly;

  // Check if user media stream has video tracks
  const hasUserVideoTracks =
    streams.userMediaStream?.getVideoTracks().length > 0;

  // Determine if we should show full-sized camera when display is not shared
  const showFullSizeCamera =
    showDisplayPreview &&
    !hasDisplayVideoTracks &&
    hasUserVideoTracks &&
    selectedDevices.videoinput.deviceId !== 'none';

  return (
    <div
      className={cn(
        'flex flex-col bg-background',
        isPopupMode ? 'p-0 mb-0' : 'p-1 mb-2',
        showLoader && (isPopupMode ? 'h-auto' : 'h-screen'),
        showLoader && 'items-center justify-center',
      )}
    >
      {!showLoader ? (
        <>
          {showDisplayPreview && (
            <div className="relative mb-4">
              <div className="relative">
                {(() => {
                  if (hasDisplayVideoTracks) {
                    return (
                      <>
                        <Display />
                        {showCameraPreview && (
                          <div className="absolute bottom-2 left-2 w-24 h-24 z-10">
                            <CameraPreview />
                          </div>
                        )}
                      </>
                    );
                  }

                  if (showFullSizeCamera) {
                    return (
                      <div className="w-full max-w-[40rem] rounded-xl overflow-hidden mx-auto mb-2 shadow-md">
                        <CameraPreview fullSize />
                      </div>
                    );
                  }
                  return null;
                })()}
              </div>
            </div>
          )}

          <div className="flex items-center justify-center gap-4 h-8">
            <div
              className={cn(
                'transition-opacity duration-300',
                isStopped ? 'opacity-0 invisible' : 'opacity-100 visible',
              )}
            >
              {showControls && (
                <ControlButtons
                  onToggleNotes={isPopupMode ? () => {} : onToggleNotes}
                  showNotes={isPopupMode ? false : showNotes}
                  isPopupMode={isPopupMode}
                />
              )}
            </div>

            <div
              className={cn(
                'transition-opacity duration-300',
                isStopped ? 'opacity-0 invisible' : 'opacity-100 visible',
              )}
            >
              {showTimer && (
                <div
                  className={cn(
                    'flex items-center px-1',
                    isPopupMode ? 'text-xs' : 'text-base',
                  )}
                >
                  <Timer />
                </div>
              )}
            </div>

            <div className="flex-1">
              <div className="w-full h-8">
                <button
                  type="button"
                  className="w-full h-full bg-background hover:bg-background relative"
                  aria-label="Audio waveform display"
                >
                  <div className="relative h-full w-full">
                    <div
                      className={cn(
                        'h-full w-full transition-opacity',
                        isRecording ? 'opacity-100' : 'opacity-0',
                      )}
                    >
                      <AudioWaveform />
                    </div>
                    <div
                      className={cn(
                        'absolute top-0 left-0 w-full h-full flex items-center justify-center bg-background/80 transition-opacity text-sm',
                        isPaused || isStopped ? 'opacity-100' : 'opacity-0',
                      )}
                    >
                      {isPaused
                        ? t('recorderControls.status.paused', 'Paused')
                        : t('recorderControls.status.saving', 'Saving...')}
                    </div>
                    {isMicrophoneMuted && isRecording && (
                      <div className="absolute top-0 left-0 w-full h-full flex items-center justify-center bg-background">
                        <div className="flex items-center gap-1 text-muted-foreground text-xs">
                          <Warning className="h-3 w-3" weight="fill" />
                          <span>
                            {t('recorderControls.status.noAudio', 'No audio')}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                </button>
              </div>
            </div>

            <div
              className={cn(
                'transition-opacity duration-300',
                isStopped ? 'opacity-0 invisible' : 'opacity-100 visible',
              )}
            >
              {showControls && (
                <TooltipProvider>
                  <div
                    className={cn(
                      'relative inline-flex items-center justify-center bg-destructive rounded-full h-8 w-8 transition-all duration-300 ease-out',
                      isControlDisabled
                        ? 'cursor-default opacity-50'
                        : 'cursor-pointer hover:bg-destructive/90',
                    )}
                  >
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <Button
                          variant="ghost"
                          onClick={stopRecordingHandler}
                          disabled={isControlDisabled}
                          id="stop-recording-button"
                          className={cn(
                            'text-white h-full w-full min-w-0 rounded-full transition-opacity duration-200 hover:bg-transparent hover:text-white disabled:text-white disabled:opacity-90 focus-visible:ring-0 flex items-center justify-center p-0',
                          )}
                          title="Stop recording"
                        >
                          <Square
                            className="h-3.5 w-3.5 text-white"
                            weight="fill"
                          />
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {t(
                          'recorderControls.tooltips.stopRecording',
                          'Stop Recording',
                        )}
                      </TooltipContent>
                    </Tooltip>
                  </div>
                </TooltipProvider>
              )}
            </div>
          </div>
        </>
      ) : (
        <div className="flex items-center justify-center h-8">
          <Loader className="h-8 w-8 animate-spin text-muted-foreground/50" />
        </div>
      )}
    </div>
  );
};

export default RecorderControls;
